import axios from "axios";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import {
  authConfig,
  esSuperAdmin,
  manejarErrorSesion,
  nombreInstructor,
  obtenerId,
} from "../utils/cobros";

const EVENTOS_MANUALES = [
  "OTRO",
  "CAMBIO_PLAN",
  "AJUSTE_FECHAS",
];

const RegistrarHistorialSuscripciones = () => {
  const navigate = useNavigate();
  const [instructores, setInstructores] = useState([]);
  const [suscripciones, setSuscripciones] = useState([]);
  const [pagos, setPagos] = useState([]);
  const [instructorObjectId, setInstructorObjectId] = useState("");
  const [suscripcionObjectId, setSuscripcionObjectId] = useState("");
  const [pagoObjectId, setPagoObjectId] = useState("");
  const [tipoEvento, setTipoEvento] = useState("OTRO");
  const [descripcion, setDescripcion] = useState("");
  const [estadoAnterior, setEstadoAnterior] = useState("");
  const [estadoNuevo, setEstadoNuevo] = useState("");
  const [metadataTexto, setMetadataTexto] = useState("{}");
  const [loading, setLoading] = useState(false);
  const [cargandoBase, setCargandoBase] = useState(false);

  const autorizado = useMemo(() => esSuperAdmin(), []);

  const suscripcionesFiltradas = useMemo(
    () => suscripciones.filter((item) => String(obtenerId(item.instructorObjectId)) === String(instructorObjectId)),
    [suscripciones, instructorObjectId]
  );

  const pagosFiltrados = useMemo(
    () => pagos.filter((item) => String(obtenerId(item.instructorObjectId)) === String(instructorObjectId)),
    [pagos, instructorObjectId]
  );

  useEffect(() => {
    if (!autorizado) return;
    const cargar = async () => {
      try {
        setCargandoBase(true);
        const [resInstructores, resSuscripciones, resPagos] = await Promise.all([
          axios.get("/api/instructores/listall", authConfig()),
          axios.get("/api/suscripciones/listall", authConfig()),
          axios.get("/api/pagos/listall", authConfig()),
        ]);
        setInstructores(Array.isArray(resInstructores.data?.instructores) ? resInstructores.data.instructores : []);
        setSuscripciones(Array.isArray(resSuscripciones.data?.suscripciones) ? resSuscripciones.data.suscripciones : []);
        setPagos(Array.isArray(resPagos.data?.pagos) ? resPagos.data.pagos : []);
      } catch (error) {
        if (manejarErrorSesion(error, navigate)) return;
        Swal.fire("Error", "No se pudieron cargar los datos relacionados.", "error");
      } finally {
        setCargandoBase(false);
      }
    };
    cargar();
  }, [autorizado, navigate]);

  const actions = async (e) => {
    e.preventDefault();
    if (!autorizado) return;
    if (!instructorObjectId || !tipoEvento || !descripcion.trim()) {
      Swal.fire("Atención", "Instructor, tipo de evento y descripción son obligatorios.", "warning");
      return;
    }

    let metadata = {};
    try {
      metadata = metadataTexto.trim() ? JSON.parse(metadataTexto) : {};
    } catch {
      Swal.fire("Metadata inválida", "El campo metadata debe contener JSON válido.", "warning");
      return;
    }

    try {
      setLoading(true);
      const { data } = await axios.post(
        "/api/historialsuscripciones/add",
        {
          instructorObjectId,
          suscripcionObjectId: suscripcionObjectId || null,
          pagoObjectId: pagoObjectId || null,
          tipoEvento,
          descripcion: descripcion.trim(),
          estadoAnterior: estadoAnterior.trim(),
          estadoNuevo: estadoNuevo.trim(),
          origen: "ADMINISTRADOR",
          metadata,
        },
        authConfig()
      );
      await Swal.fire({ icon: "success", title: data?.message || "Evento registrado", showConfirmButton: false, timer: 1600 });
      navigate("/cobros/historial");
    } catch (error) {
      if (manejarErrorSesion(error, navigate)) return;
      Swal.fire("Error", error.response?.data?.message || "No se pudo registrar el evento.", "error");
    } finally {
      setLoading(false);
    }
  };

  if (!autorizado) {
    return (
      <div className="container py-5">
        <div className="alert alert-warning">El registro manual de auditoría está reservado para SUPER_ADMIN. Los eventos normales se generan automáticamente.</div>
        <button className="btn btn-dark" type="button" onClick={() => navigate("/cobros/historial")}>Volver</button>
      </div>
    );
  }

  return (
    <div className="container-fluid px-2 px-md-4">
      <div className="bg-white border rounded-4 shadow-sm p-3 p-md-4 mb-3">
        <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
          <div>
            <h4 className="mb-1 fw-semibold">Registrar Evento de Historial</h4>
            <p className="mb-0 text-secondary small">Uso excepcional de SUPER_ADMIN. No existe relación con solicitudes de renovación.</p>
          </div>
          <button className="btn btn-outline-dark" type="button" onClick={() => navigate("/cobros/historial")}>Volver</button>
        </div>
      </div>

      <form onSubmit={actions} className="bg-white border rounded-4 shadow-sm p-3 p-md-4">
        <div className="row g-3">
          <div className="col-12">
            <label className="form-label small text-secondary text-uppercase">Instructor *</label>
            <select className="form-select" value={instructorObjectId} onChange={(e) => { setInstructorObjectId(e.target.value); setSuscripcionObjectId(""); setPagoObjectId(""); }} disabled={cargandoBase || loading} required>
              <option value="">Seleccione</option>
              {instructores.map((item) => <option key={item._id} value={item._id}>{nombreInstructor(item)} - {item.documentoInstructor || "Sin documento"}</option>)}
            </select>
          </div>
          <div className="col-12 col-md-6">
            <label className="form-label small text-secondary text-uppercase">Suscripción relacionada</label>
            <select className="form-select" value={suscripcionObjectId} onChange={(e) => setSuscripcionObjectId(e.target.value)} disabled={!instructorObjectId || loading}>
              <option value="">Sin relación</option>
              {suscripcionesFiltradas.map((item) => <option key={item._id} value={item._id}>{item.planObjectId?.nombrePlan || "Plan"} · {String(item.estadoSuscripcion || "").replaceAll("_", " ")}</option>)}
            </select>
          </div>
          <div className="col-12 col-md-6">
            <label className="form-label small text-secondary text-uppercase">Pago relacionado</label>
            <select className="form-select" value={pagoObjectId} onChange={(e) => setPagoObjectId(e.target.value)} disabled={!instructorObjectId || loading}>
              <option value="">Sin relación</option>
              {pagosFiltrados.map((item) => <option key={item._id} value={item._id}>{item.conceptoPago || "PAGO"} · {item.estadoPago || ""} · {item.referenciaPago || item._id}</option>)}
            </select>
          </div>
          <div className="col-12 col-md-6">
            <label className="form-label small text-secondary text-uppercase">Evento *</label>
            <select className="form-select" value={tipoEvento} onChange={(e) => setTipoEvento(e.target.value)} disabled={loading} required>
              {EVENTOS_MANUALES.map((evento) => <option key={evento} value={evento}>{evento.replaceAll("_", " ")}</option>)}
            </select>
          </div>
          <div className="col-12 col-md-3">
            <label className="form-label small text-secondary text-uppercase">Estado anterior</label>
            <input className="form-control" value={estadoAnterior} onChange={(e) => setEstadoAnterior(e.target.value.toUpperCase())} disabled={loading} />
          </div>
          <div className="col-12 col-md-3">
            <label className="form-label small text-secondary text-uppercase">Estado nuevo</label>
            <input className="form-control" value={estadoNuevo} onChange={(e) => setEstadoNuevo(e.target.value.toUpperCase())} disabled={loading} />
          </div>
          <div className="col-12">
            <label className="form-label small text-secondary text-uppercase">Descripción *</label>
            <textarea className="form-control" rows="4" value={descripcion} onChange={(e) => setDescripcion(e.target.value)} disabled={loading} required />
          </div>
          <div className="col-12">
            <label className="form-label small text-secondary text-uppercase">Metadata JSON</label>
            <textarea className="form-control font-monospace" rows="4" value={metadataTexto} onChange={(e) => setMetadataTexto(e.target.value)} disabled={loading} />
          </div>
          <div className="col-12">
            <button className="btn btn-dark w-100" type="submit" disabled={loading || cargandoBase}>{loading ? "Guardando…" : "Registrar evento"}</button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default RegistrarHistorialSuscripciones;
